import {CreativeStatusType, TableSortState} from 'bigdatr-style';
import {CreativeListOrderBy} from '~/feature/filter/CreativeSortBy';
import {MediaValueColumn} from '~/feature/filter/types';
import {CreativeDisplay} from '~/feature/filter/useCreativeListDisplayState';

import {
    ReportItemV2 as GraphqlReportItem,
    FilterColumnV2,
    MediaValueDateRangeFilter,
    PeriodSelection,
    ReportItemV2CreateInput
} from '~/graphql-react-query/graphql';

// Definitions
export const chartTypes = ['Stacked', 'Stacked %', 'Line', 'Matrix', 'Column', 'Ranking'] as const;
export type ChartType = (typeof chartTypes)[number];
export function isChartType(x: string): x is ChartType {
    return !!chartTypes.find((tt) => x === tt);
}

export type ColumnFilterV2 = {[K in FilterColumnV2]?: Array<string>};

export type AbsoluteDateRange = {startDate: string; endDate: string};
export type MediaDateRangeFilter = {
    truncateMonths?: number;
    absolute?: AbsoluteDateRange;
    relative?: {months: number; includeForecasted: boolean};
};

//
// Media value
//

//
// Breakdown
export type BreakdownColumnFilterUpsert = {
    segment?: string[];
    adType?: string[];
    brand?: string[];
    campaign?: string[];
    category?: string[];
    industry?: string[];
    mediaOwner?: string[];
    mediaType?: string[];
    product?: string[];
    publisher?: string[];
    publication?: string[];
    region?: string[];
};

export type BreakdownColumnFilterDefinition = {
    segment?: {name: string; id: string}[];
    adType?: {name: string; id: string}[];
    brand?: {name: string; id: string}[];
    campaign?: {name: string; id: string}[];
    category?: {name: string; id: string}[];
    industry?: {name: string; id: string}[];
    mediaOwner?: {name: string; id: string}[];
    mediaType?: {name: string; id: string}[];
    product?: {name: string; id: string}[];
    publisher?: {name: string; id: string}[];
    publication?: {name: string; id: string}[];
    region?: {name: string; id: string}[];
};

export type BreakdownDefinition = {
    filter: {
        columns: MediaValueColumn[];
        excludedColumns?: MediaValueColumn[];
        columnFilter: BreakdownColumnFilterDefinition;
        dateRangeFilter: MediaDateRangeFilter;
    };
    chartType: ChartType;
};

export type BreakdownDefinitionUpsert = {
    filter: {
        columns: MediaValueColumn[];
        excludedColumns?: MediaValueColumn[];
        columnFilter: BreakdownColumnFilterUpsert;
        dateRangeFilter: MediaDateRangeFilter;
    };
    chartType: ChartType;
};

export type ComparisonRowItemDefinition = {
    id: string;
    label: string;
    filter: {
        adType?: {name: string; id: string}[];
        brand?: {name: string; id: string}[];
        category?: {name: string; id: string}[];
        industry?: {name: string; id: string}[];
        mediaOwner?: {name: string; id: string}[];
        mediaType?: {name: string; id: string}[];
        product?: {name: string; id: string}[];
        publisher?: {name: string; id: string}[];
        region?: {name: string; id: string}[];
    };
};

export type ComparisonRowItemUpsertDefinition = {
    id: string;
    label: string;
    filter: {
        adType?: string[];
        brand?: string[];
        category?: string[];
        industry?: string[];
        mediaOwner?: string[];
        mediaType?: string[];
        product?: string[];
        publisher?: string[];
        region?: string[];
    };
};

export type ComparisonDefinition = {
    periodSelection: PeriodSelection;
    rows: ComparisonRowItemDefinition[];
};

export type ComparisonUpsertDefinition = {
    periodSelection: PeriodSelection;
    rows: ComparisonRowItemUpsertDefinition[];
};

export type DistributionBreakdownColumn =
    | 'adType'
    | 'brand'
    | 'category'
    | 'industry'
    | 'mediaOwner'
    | 'mediaType'
    | 'product'
    | 'publisher'
    | 'region';

export type DistributionRowDefinition = {
    id: string;
    label: string;
    filter: {
        adType?: {id: string; name: string}[];
        brand?: {id: string; name: string}[];
        category?: {id: string; name: string}[];
        industry?: {id: string; name: string}[];
        mediaOwner?: {id: string; name: string}[];
        mediaType?: {id: string; name: string}[];
        product?: {id: string; name: string}[];
        publisher?: {id: string; name: string}[];
        region?: {id: string; name: string}[];
    };
};

export type DistributionRowUpsertDefinition = {
    id: string;
    label: string;
    filter: {
        adType?: string[];
        brand?: string[];
        category?: string[];
        industry?: string[];
        mediaOwner?: string[];
        mediaType?: string[];
        product?: string[];
        publisher?: string[];
        region?: string[];
    };
};

export type DistributionDefinition = {
    dateRangeFilter: MediaValueDateRangeFilter;
    breakdownColumn: DistributionBreakdownColumn;
    rows: DistributionRowDefinition[];
};

export type DistributionUpsertDefinition = {
    dateRangeFilter: MediaValueDateRangeFilter;
    breakdownColumn: DistributionBreakdownColumn;
    rows: DistributionRowUpsertDefinition[];
};

export type HtmlEmbedDefinition = {
    html: string;
};

//
// Creative
//

export type CreativeDateRangeFilter = {
    absolute?: AbsoluteDateRange | null;
    relative?: {months: number} | null;
};

/** @deprecated we use `CreativeDateRangeFilter now, but still need it in-app because
 * reports can have old definitions in them*/
export type CreativeDateRange = {
    min: string;
    max: string;
};

export type CreativeColumns =
    | 'adType'
    | 'brand'
    | 'campaign'
    | 'category'
    | 'industry'
    | 'mediaOwner'
    | 'mediaType'
    | 'placementType'
    | 'product'
    | 'publication'
    | 'publisher'
    | 'region'
    | 'segment'
    | 'status'
    | 'aiAdTypeV1'
    | 'aiAdvertiserV1'
    | 'aiCategoryV1'
    | 'aiIndustryV1'
    | 'aiProductV1'
    | 'aiFuelTypeV1'
    // Report API column name; mapped from aiFuelTypeV1 only when saving
    | 'fuelType';

export type AdvertisingCreativeDefinition = {
    columns: CreativeColumns[];
    filter: {
        dateRangeFilter?: CreativeDateRangeFilter;
        status?: CreativeStatusType[];
        placementType?: string[];
        searchQuery?: string;
        advertiserName?: string[];
        advertiserDomain?: string[];

        // entities
        adType?: {name: string; id: string}[];
        campaign?: {name: string; id: string}[];
        industry?: {name: string; id: string}[];
        category?: {name: string; id: string; industry?: {id: string; name: string}}[];
        mediaOwner?: {name: string; id: string}[];
        mediaType?: {name: string; id: string}[];
        product?: {name: string; id: string}[];
        publication?: {name: string; id: string}[];
        publisher?: {name: string; id: string}[];
        region?: {name: string; id: string}[];
        brand?: {name: string; id: string}[];
        segment?: {name: string; id: string}[];

        aiAdTypeV1?: string[];
        aiAdvertiserV1?: string[];
        aiCategoryV1?: string[];
        aiIndustryV1?: string[];
        aiProductV1?: string[];
        // Present on report API responses; convertCreativeDefinitionToFilter maps to aiFuelTypeV1
        fuelType?: string[];
    };
    chartType: CreativeDisplay;
    orderBy?: CreativeListOrderBy;
    tableSort?: TableSortState;
};

export type CreativeReportItemUpsertInput = {
    columns: CreativeColumns[];
    filter: {
        dateRangeFilter?: CreativeDateRangeFilter;
        status?: CreativeStatusType[];
        placementType?: string[];
        adType?: string[];
        campaign?: string[];
        industry?: string[];
        category?: string[];
        mediaOwner?: string[];
        mediaType?: string[];
        product?: string[];
        publication?: string[];
        publisher?: string[];
        region?: string[];
        brand?: string[];
        segment?: string[];
        searchQuery?: string;
        advertiserName?: string[];
        advertiserDomain?: string[];
        aiAdTypeV1?: string[];
        aiAdvertiserV1?: string[];
        aiCategoryV1?: string[];
        aiIndustryV1?: string[];
        aiProductV1?: string[];
        aiFuelTypeV1?: string[];
        // Only present after mapCreativeDefinitionToReportApi (API write edge)
        fuelType?: string[];
    };
    chartType: CreativeDisplay;
    orderBy?: CreativeListOrderBy;
    tableSort?: TableSortState;
};

//
//
// Items

export type BreakdownItem = Omit<GraphqlReportItem, 'type' | 'definitionV2'> & {
    type: 'MediaValue';
    definitionV2: BreakdownDefinition;
};

export type DistributionItem = Omit<GraphqlReportItem, 'type' | 'definitionV2'> & {
    type: 'Distribution';
    definitionV2: DistributionDefinition;
};

export type ComparisonItem = Omit<GraphqlReportItem, 'type' | 'definitionV2'> & {
    type: 'Comparison';
    definitionV2: ComparisonDefinition;
};

export type AdvertisingCreativeItem = Omit<GraphqlReportItem, 'type' | 'definitionV2'> & {
    type: 'AdvertisingCreative';
    definitionV2: AdvertisingCreativeDefinition;
};

export type EmbedItem = Omit<GraphqlReportItem, 'type' | 'definitionV2'> & {
    type: 'Embed';
    definitionV2: HtmlEmbedDefinition;
};

export type ReportItem =
    | BreakdownItem
    | AdvertisingCreativeItem
    | ComparisonItem
    | DistributionItem
    | EmbedItem;
export type ReportItemInput =
    | BreakdownUpsertInput
    | AdvertisingCreativeUpsertInput
    | ComparisonUpsertInput
    | DistributionUpsertInput
    | EmbedInput;

//
//
// Input Types

export type BreakdownUpsertInput = Omit<ReportItemV2CreateInput, 'definitionV2'> & {
    type: 'MediaValue';
    definitionV2: BreakdownDefinitionUpsert;
    id?: string;
};

export type AdvertisingCreativeUpsertInput = Omit<ReportItemV2CreateInput, 'definitionV2'> & {
    type: 'AdvertisingCreative';
    definitionV2: CreativeReportItemUpsertInput;
    id?: string;
};

export type ComparisonUpsertInput = Omit<ReportItemV2CreateInput, 'definitionV2'> & {
    type: 'Comparison';
    definitionV2: ComparisonUpsertDefinition;
    id?: string;
};

export type DistributionUpsertInput = Omit<ReportItemV2CreateInput, 'definitionV2'> & {
    type: 'Distribution';
    definitionV2: DistributionUpsertDefinition;
    id?: string;
};

export type EmbedInput = Omit<ReportItemV2CreateInput, 'definitionV2'> & {
    type: 'Embed';
    definitionV2: HtmlEmbedDefinition;
    id?: string;
};

//
// draft types - drafts have everything optional
export type BreakdownDraftItem = {
    type: 'MediaValue';
    definitionV2?: BreakdownDefinitionUpsert;
    description?: string;
    name?: string;
};

export type AdvertisingCreativeDraftItem = {
    type: 'AdvertisingCreative';
    definitionV2?: CreativeReportItemUpsertInput;
    description?: string;
    name?: string;
};

export type ComparisonDraftItem = {
    type: 'Comparison';
    definitionV2?: ComparisonUpsertDefinition;
    description?: string;
    name?: string;
};

export type DistributionDraftItem = {
    type: 'Distribution';
    definitionV2?: DistributionUpsertDefinition;
    description?: string;
    name?: string;
};

export type EmbedDraftItem = {
    type: 'Embed';
    definitionV2?: HtmlEmbedDefinition;
    description?: string;
    name?: string;
};
export type DraftReportItem =
    | BreakdownDraftItem
    | AdvertisingCreativeDraftItem
    | ComparisonDraftItem
    | DistributionDraftItem
    | EmbedDraftItem;

//
// Route Args
export type ReportRouteArgs = {
    id: string;
    draftReportItem?: DraftReportItem;
    triggerCreateModal?: boolean;
};
